import { useState, useMemo, useRef, useEffect } from 'react';
import { X, Search, Check, Globe, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PREFIXES, SUPPORTED_LANGS } from '@/i18n/routingUtils';
import { useCurrency } from '@/shared/context/CurrencyContext';
import { LoadingSpinner } from '@/components/ui';
import { FOCUS_DELAY_MS } from '@/shared/lib/constants';

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe', flag: '🇹🇷' },
  { code: 'pt', name: 'Portuguese', native: 'Português', flag: '🇵🇹' },
  { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands', flag: '🇳🇱' },
];

// Pinned at the top regardless of search
const POPULAR_CURRENCIES = ['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'TRY', 'CNY', 'INR'];
const POPULAR_LANGS = ['en', 'es', 'fr', 'it'];

export function PreferencesModal() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'currency' | 'language'>('currency');
  const [search, setSearch] = useState('');
  const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [panelStyle, setPanelStyle] = useState<{ right: number; width: number } | null>(null);

  const { selectedCurrency, setCurrency, currencies, loading: currencyLoading } = useCurrency();

  useEffect(() => {
    if (isOpen && activeTab === 'currency') {
      setTimeout(() => searchRef.current?.focus(), FOCUS_DELAY_MS);
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [isOpen]);

  // Keep the panel right-anchored to the trigger, but clamp it so it never
  // overflows past the left edge of the viewport on narrow screens.
  useEffect(() => {
    if (!isOpen) return;
    const compute = () => {
      if (!panelRef.current) return;
      const margin = 8;
      // panelRef's right edge (viewport coords) equals the trigger button's right
      // edge, since the dropdown is the only absolutely-positioned child.
      const triggerRight = panelRef.current.getBoundingClientRect().right;
      const width = Math.min(480, window.innerWidth - margin * 2);
      const maxRightEdge = window.innerWidth - margin;
      const minRightEdge = width + margin;
      const desiredRightEdge = Math.min(triggerRight, maxRightEdge);
      const rightEdge = Math.max(desiredRightEdge, minRightEdge);
      // `right` is expressed relative to the trigger's right edge (0 = flush,
      // as before); it goes negative once we need to shift the panel rightward
      // to keep its left edge on screen.
      setPanelStyle({ right: triggerRight - rightEdge, width });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('resize', compute);
    };
  }, [isOpen]);

  const allCurrencies = useMemo(
    () =>
      Object.entries(currencies)
        .map(([code, meta]) => ({ code, ...meta }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [currencies]
  );

  // Split into popular + rest, filtered by search
  const { popular, rest } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? allCurrencies.filter(
          (c) =>
            c.code.toLowerCase().includes(q) ||
            c.name.toLowerCase().includes(q) ||
            (c as any).country_name?.toLowerCase().includes(q) ||
            false
        )
      : allCurrencies;

    const popularSet = new Set(POPULAR_CURRENCIES);
    return {
      popular: list
        .filter((c) => popularSet.has(c.code))
        .sort((a, b) => POPULAR_CURRENCIES.indexOf(a.code) - POPULAR_CURRENCIES.indexOf(b.code)),
      rest: list.filter((c) => !popularSet.has(c.code)),
    };
  }, [allCurrencies, search]);

  const { popularLangs, restLangs } = useMemo(() => {
    const popularSet = new Set(POPULAR_LANGS);
    return {
      popularLangs: LANGUAGES.filter((l) => popularSet.has(l.code)),
      restLangs: LANGUAGES.filter((l) => !popularSet.has(l.code)),
    };
  }, []);

  const selectedMeta = currencies[selectedCurrency.code];
  const displayFlag = selectedMeta?.flag ?? selectedCurrency.flag;
  const selectedLang = LANGUAGES.find((l) => l.code === currentLang) ?? LANGUAGES[0];

  const open = () => {
    setIsOpen(true);
    setSearch('');
  };
  const close = () => {
    setIsOpen(false);
  };

  const CurrencyBtn = ({ curr }: { curr: (typeof popular)[0] }) => {
    const isActive = selectedCurrency.code === curr.code;
    return (
      <button
        key={curr.code}
        onClick={() => {
          setCurrency(curr.code);
          close();
        }}
        className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-all ${isActive ? 'bg-slate-50 ring-1 ring-slate-200 shadow-sm' : 'hover:bg-gray-50'}`}
      >
        <span className="shrink-0 text-xl leading-none">{curr.flag}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-black text-gray-900">{curr.code}</span>
            <span className="text-[10px] text-gray-400">{curr.symbol}</span>
          </div>
          <div className="text-[10px] text-gray-400 truncate">{(curr as any).country_name ?? curr.name}</div>
        </div>
        {isActive && <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-600" />}
      </button>
    );
  };

  const LangBtn = ({ lang }: { lang: (typeof LANGUAGES)[0] }) => {
    const isActive = currentLang === lang.code;
    return (
      <button
        key={lang.code}
        onClick={() => {
          const path = location.pathname;
          let currentPrefix = '';
          for (const prefix of SUPPORTED_LANGS) {
            if (path.startsWith(`/${prefix}/`) || path === `/${prefix}`) {
              currentPrefix = prefix;
              break;
            }
          }

          let strippedPath = path;
          if (currentPrefix) {
            strippedPath = path.substring(currentPrefix.length + 1) || '/';
          }
          if (!strippedPath.startsWith('/')) {
            strippedPath = '/' + strippedPath;
          }

          const targetPrefix = ROUTE_PREFIXES[lang.code] || '';
          const newPath = targetPrefix ? `/${targetPrefix}${strippedPath === '/' ? '' : strippedPath}` : strippedPath;

          void i18n.changeLanguage(lang.code);
          close();
          navigate(newPath + location.search + location.hash);
        }}
        className={`flex items-center gap-2.5 rounded-xl px-3 py-3 text-left transition-all ${isActive ? 'bg-slate-50 ring-1 ring-slate-200 shadow-sm' : 'hover:bg-gray-50'}`}
      >
        <span className="shrink-0 text-xl leading-none">{lang.flag}</span>
        <div className="min-w-0 flex-1">
          <div className="nav-label text-gray-900">{lang.name}</div>
          <div className="text-[11px] text-gray-400">{lang.native}</div>
        </div>
        {isActive && <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-600" />}
      </button>
    );
  };

  return (
    <div ref={panelRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        id="locale-selector-trigger"
        onClick={() => {
          isOpen ? close() : open();
        }}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-slate-600 transition-all hover:bg-gray-50 hover:text-slate-950 active:scale-95 shadow-sm"
        aria-label={t('common.regionalPreferences')}
      >
        <Globe className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={close} />

          <div
            className="absolute top-full z-[90] mt-2 animate-in fade-in slide-in-from-top-2 duration-200"
            style={panelStyle ? { right: panelStyle.right, width: panelStyle.width } : { right: 0, width: 480 }}
          >
            <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06]">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="heading-4">{t('common.regionalPreferences')}</h3>
                  <p className="mt-0.5 text-[11px] text-gray-400">{t('common.regionalSub')}</p>
                </div>
                <button
                  onClick={close}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-100">
                {(
                  [
                    { key: 'currency', label: t('common.currency'), icon: DollarSign },
                    { key: 'language', label: t('common.language'), icon: Globe },
                  ] as const
                ).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setActiveTab(key);
                      setSearch('');
                    }}
                    className={`flex flex-1 items-center justify-center gap-2 py-3 text-xs font-black transition-colors ${
                      activeTab === key
                        ? 'border-b-2 border-slate-900 text-slate-900'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* ── Currency Tab ── */}
              {activeTab === 'currency' && (
                <>
                  {/* Search */}
                  <div className="border-b border-gray-50 px-4 py-3">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                      <input
                        ref={searchRef}
                        type="text"
                        placeholder={t('common.searchPlaceholder')}
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                        }}
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-xs font-medium text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  {currencyLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <LoadingSpinner minHeight="min-h-0" size="h-6 w-6" />
                    </div>
                  ) : (
                    <div className="overflow-y-auto px-3 py-3 max-h-[340px]">
                      {popular.length === 0 && rest.length === 0 ? (
                        <p className="py-8 text-center desc-sm !text-gray-400">{t('common.noCurrencies')}</p>
                      ) : (
                        <>
                          {popular.length > 0 && (
                            <>
                              <p className="mb-1.5 px-1 eyebrow text-gray-400">{t('common.popular')}</p>
                              <div className="grid grid-cols-2 gap-1 pb-3">
                                {popular.map((c) => (
                                  <CurrencyBtn key={c.code} curr={c} />
                                ))}
                              </div>
                            </>
                          )}

                          {rest.length > 0 && (
                            <>
                              <div className="my-1.5 border-t border-gray-100" />
                              <p className="mb-1.5 mt-3 px-1 eyebrow text-gray-400">{t('common.allCurrencies')}</p>
                              <div className="grid grid-cols-2 gap-1">
                                {rest.map((c) => (
                                  <CurrencyBtn key={c.code} curr={c} />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Language Tab ── */}
              {activeTab === 'language' && (
                <div className="overflow-y-auto px-3 py-3 max-h-[380px]">
                  <p className="mb-1.5 px-1 eyebrow text-gray-400">{t('common.popular')}</p>
                  <div className="grid grid-cols-2 gap-1 pb-3">
                    {popularLangs.map((l) => (
                      <LangBtn key={l.code} lang={l} />
                    ))}
                  </div>

                  <div className="my-1.5 border-t border-gray-100" />
                  <p className="mb-1.5 mt-3 px-1 eyebrow text-gray-400">{t('common.allLanguages')}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {restLangs.map((l) => (
                      <LangBtn key={l.code} lang={l} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
